/* eslint-disable complexity */
import React, { type FormEvent, memo, useCallback, useEffect, useRef, useState } from "react";

import SearchInput from "~/features/search/components/Autosuggest/SearchInput";

import { type ContentDataItem, type SrpProductData } from "~/utils/models";
import { AUTOSUGGEST_DEBOUNCE_TIME, AUTOSUGGEST_MAX_LENGTH } from "~/utils/constants";
import { useDispatch, useSelector } from "react-redux";
import { updateSearchText } from "~/features/search/redux/actions";
import { sendSearchBoxSearch, trackSubmittedSearch } from "~/app/analytics/ga/ga";
import debounce from "lodash/debounce";
import { type AutosuggestProductGroup } from "~/domains/navigation/types/NavigationTypes";
import { fetchAutosuggestionList } from "~/features/search/api/fetchAutosuggestions";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { type LoadableEntry } from "~/app/store/loadableTypes";
import { getNextUID } from "~/common/utils/uid";
import { addBaseProductDataAction } from "~/domains/product/redux/actions";
import RecentSearches, {
  type SearchEntry,
} from "~/features/search/components/Autosuggest/RecentSearches/RecentSearches";
import { Box, ClickAwayListener } from "~/common/mui";
import { useTranslation } from "react-i18next";
import classes from "./SearchBox.module.less";
import classNames from "classnames";
import SearchProductResults from "~/features/search/components/Autosuggest/SearchProductResults/SearchProductResults";
import SearchRelatedResults from "~/features/search/components/Autosuggest/SearchRelatedResults/SearchRelatedResults";
import { type AllScreenSizes, useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import { getCsrfValue } from "~/common/utils/ApiHelper";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { saveSearchTerm } from "~/features/search/api/saveSearchTerm";
import { redirectToUrl } from "~/utils/url/urlUtils";
import type { RootState } from "~/app/store";
import { setSearchQuery } from "~/features/search/redux/autosuggest/actions";
import { isSafari } from "~/common/utils/browserChecks";
import { AnimatedText, Placeholder, StaticText, useTypeWriter } from "~/common/hooks/useTypeWriter";

interface Props {
  recentSearches: boolean;
  showRecentSearches: () => void;
  hideRecentSearches: () => void;
  suggestions: boolean;
  showSuggestions: () => void;
  hideSuggestions: () => void;
  isSearchPage: boolean;
  topBar?: boolean;
  displayTopBar?: () => void;
  hideTopBar?: () => void;
  showOverlay: () => void;
  hideOverlay: () => void;
  overlay: boolean;
}

const ResultsBox = ({
  children,
  hideOverlay,
  showOverlay,
  topBar,
}: {
  children?: React.ReactNode;
  showOverlay: () => void;
  hideOverlay: () => void;
  topBar?: boolean;
}) => {
  const isTablet = useIsMinTabletSize();

  const hasRenderedChildren = React.Children.toArray(children).filter(Boolean).length > 0;
  const showBackground = !topBar && !isTablet;
  useEffect(() => {
    if (hasRenderedChildren) {
      showOverlay();
    } else {
      hideOverlay();
    }
  }, [hasRenderedChildren]);

  return (
    <>
      <div className={classNames(!hasRenderedChildren && !showBackground && "v-hidden", classes.resultsBox)}>
        {children}
      </div>
    </>
  );
};

const SearchBox = ({
  recentSearches,
  showRecentSearches,
  hideRecentSearches,
  suggestions,
  showSuggestions,
  hideSuggestions,
  isSearchPage,
  topBar,
  displayTopBar,
  hideTopBar,
  showOverlay,
  hideOverlay,
  overlay,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const animatedPlaceholders = t("searchbox.animated.placeholders");
  const text = useSelector((state: RootState) => state.search.query);
  const animatedText = useTypeWriter({
    enabled: !text, // disabled when user types
    words: animatedPlaceholders.split(";"),
  });

  const numberOfProducts = useSelector((state: RootState) => state.srp.results.pagination.totalNumberOfResults);
  const relatedContentResults = useSelector(
    (state: RootState) => state.srp.content.contentPagination.totalNumberOfResults,
  );
  const setText = (query: string) => dispatch(setSearchQuery(query));

  const onSearch = (searchText: string) => {
    searchText = searchText.trim();
    updateBreadcrumbWithQuery(searchText);
    updateDocumentWithQuery(searchText);
    dispatch(updateSearchText(searchText));
  };

  const {
    val: isHoverSuggestion,
    setFalse: mouseLeaveSuggestionList,
    setTrue: mouseEnterSuggestionList,
  } = useBooleanState(false);

  const formRef = useRef<HTMLFormElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const data = useFetchData(text);

  const submit = (e?: FormEvent<HTMLFormElement>) => {
    const params = new URLSearchParams();
    params.set("text", text);
    const SEARCH_PAGE_URL = "/search/searchpage";
    const url = `${SEARCH_PAGE_URL}?${params.toString()}`;
    e?.preventDefault();
    sendSearchBoxSearch(text);
    trackSubmittedSearch(text, numberOfProducts + relatedContentResults, numberOfProducts, relatedContentResults);
    if (isSearchPage) {
      onSearch(text);
      (document.activeElement as HTMLElement).blur();
    }
    hideAllContent();
    if (displayTopBar) {
      displayTopBar();
    }
    if (text) {
      saveSearchTerm(text, url).then(() => {
        if (!isSearchPage) {
          redirectToUrl(url);
        }
      });
    }
  };

  const showSuggestionList =
    text &&
    text.length >= 2 &&
    (suggestions || isHoverSuggestion) &&
    (data.data?.products.length || data.data?.productGroups?.length || data.data?.contentItems?.length || false);

  const hideAllContent = () => {
    hideOverlay();
    hideRecentSearches();
    hideSuggestions();
    inputRef.current?.blur();
  };

  const [searchResults, setSearchResults] = useState<{
    recentSearches?: SearchEntry[];
    popularSearches?: SearchEntry[];
    state: "loading" | "loaded" | "error";
  }>({ state: "loading" });

  const removeFromSearchResults = (term: string) => {
    setSearchResults((prevState) => ({
      ...prevState,
      recentSearches: prevState?.recentSearches?.filter((entry) => entry.term !== term),
    }));
  };

  const emptySearchResults = !searchResults?.popularSearches?.length && !searchResults?.recentSearches?.length;

  useEffect(() => {
    setSearchResults({ state: "loading" });
    if (recentSearches) {
      fetchSuggestionResults().then((data) => {
        setSearchResults({ popularSearches: data.popularTerms, recentSearches: data.recentSearches, state: "loaded" });
      });
    }
  }, [recentSearches]);

  return (
    <div className="scha__search-box">
      <form onSubmit={submit} ref={formRef}>
        <ClickAwayListener
          mouseEvent={overlay && !isSafari() ? "onMouseUp" : "onMouseDown"}
          touchEvent={overlay ? "onTouchEnd" : "onTouchStart"}
          onClickAway={() => {
            inputRef.current?.blur();

            if (displayTopBar) {
              displayTopBar();
            }
            if (showSuggestionList) {
              hideSuggestions();
            } else {
              hideRecentSearches();
            }
          }}
        >
          <div>
            <Box sx={{ position: "relative", width: "100%", overflow: "hidden" }}>
              {!text && (
                <Placeholder variant="bodyLarge">
                  <StaticText>{t("searchbox.static.placeholders")}&nbsp;</StaticText>
                  <AnimatedText>{animatedText}</AnimatedText>
                </Placeholder>
              )}
              <SearchInput
                variant={"header"}
                ref={inputRef}
                value={text}
                onChange={setText}
                onClear={() => setText("")}
                onFocus={() => {
                  showSuggestions();
                  if (hideTopBar) {
                    hideTopBar();
                  }
                  if (!text) {
                    showRecentSearches();
                  }
                }}
              />
            </Box>

            <ResultsBox hideOverlay={hideOverlay} showOverlay={showOverlay} topBar={topBar}>
              {!text && recentSearches && !emptySearchResults && (
                <RecentSearches
                  searchData={searchResults}
                  removeEntry={removeFromSearchResults}
                  hide={hideAllContent}
                />
              )}
              {showSuggestionList &&
                (data.data?.designationMatch ? (
                  <SearchProductResults
                    state={data.state}
                    query={text}
                    results={data.data?.products}
                    groups={data.data?.productGroups}
                    hide={hideAllContent}
                  />
                ) : (
                  <SearchRelatedResults
                    state={data.state}
                    query={text}
                    products={data.data?.products}
                    groups={data.data?.productGroups}
                    suggestions={data.data?.searchSuggestions}
                    assets={data.data?.contentItems}
                    hide={hideAllContent}
                  />
                ))}
            </ResultsBox>
          </div>
        </ClickAwayListener>
      </form>
    </div>
  );
};

export default memo(SearchBox);

export const updateDocumentWithQuery = (query: string) => {
  const titleParts = document.title.split(" ");
  titleParts[1] = query;
  document.title = titleParts.join(" ");
};

export const updateBreadcrumbWithQuery = (query: string) => {
  const element = document.querySelector(".js__breadcrumb-active-label");
  if (element) {
    element.innerHTML = query;
  }
};

export interface Data {
  products: SrpProductData[];
  productGroups: AutosuggestProductGroup[];
  contentItems: ContentDataItem[];
  totalResults?: number;
  recentSearches?: SearchEntry[];
  popularTerms?: SearchEntry[];
  designationMatch?: boolean;
  searchSuggestions?: SearchEntry[];
}

const NO_RESULTS: Data = {
  products: [],
  productGroups: [],
  contentItems: [],
};

const fetchData = async (query: string, isCad?: boolean): Promise<Data> => {
  if (query.length <= 1) {
    return NO_RESULTS;
  }

  const results = await fetchAutosuggestionList(query, isCad);

  return {
    products: results.data.slice(0, AUTOSUGGEST_MAX_LENGTH),
    productGroups: results.productGroups.slice(0, AUTOSUGGEST_MAX_LENGTH),
    contentItems: results.contentItems.slice(0, AUTOSUGGEST_MAX_LENGTH),
    totalResults: results.totalResults,
    designationMatch: results.designationMatch,
    searchSuggestions: results.searchSuggestions.slice(0, AUTOSUGGEST_MAX_LENGTH),
  };
};

export const useFetchData = (query: string, isCad?: boolean): LoadableEntry<Data> => {
  const [data, setData] = useState<LoadableEntry<Data>>({ state: "loading" });
  const dispatch = useDispatch();

  const guidRef = useRef(-1);

  const fetchDataCb = useCallback(
    debounce((query: string) => {
      const guid = getNextUID();
      guidRef.current = guid;

      setData((prev) => {
        if (prev.state !== "loading") {
          return { ...prev, state: "loading" };
        }
        return prev;
      });

      fetchData(query, isCad)
        .then((res: Data) => {
          if (res?.products.length > 0) {
            dispatch(addBaseProductDataAction(res.products));
          }
          if (guidRef.current === guid) {
            setData({ state: "loaded", data: res });
          }
        })
        .catch(() => {
          if (guidRef.current === guid) {
            setData({ state: "error" });
          }
        });
    }, AUTOSUGGEST_DEBOUNCE_TIME),
    [],
  );

  useEffect(() => {
    fetchDataCb(query);
  }, [query]);

  return data;
};

const PLACEHOLDER_TEXTS: Record<AllScreenSizes, string> = {
  [useNumericScreenSize.belowPhone]: "recentSearches.placeholder.mobile",
  [useNumericScreenSize.phone]: "recentSearches.placeholder.mobile",
  [useNumericScreenSize.tablet]: "recentSearches.placeholder.tablet",
  [useNumericScreenSize.desktopS]: "recentSearches.placeholder.desktop",
  [useNumericScreenSize.desktopM]: "recentSearches.placeholder.desktop",
  [useNumericScreenSize.desktopL]: "recentSearches.placeholder.desktop",
};

const fetchSuggestionResults = async () => {
  const response = await fetch(resolveApiUrl(`/search/header/autosuggest`), {
    credentials: "same-origin",
    mode: "cors",
    referrer: "no-referrer",
    headers: {
      "content-type": "application/json",
      CSRFToken: getCsrfValue(),
    },
    method: "GET",
  });

  return (await response.json()) as Data;
};
